// near-submit-btc-transfer — "unstick" a NEAR→UTXO withdrawal whose relayer
// never submitted it to the chain connector after the user's init_transfer.
//
// Hybrid: the backend scrapes the origin init_transfer NEAR tx for the
// transfer id / target address / net amount (the browser has no NEAR
// tx_status RPC), then the frontend selects UTXOs via @omni-bridge/btc and
// builds `submit_transfer_to_utxo_chain_connector` on the omni-bridge
// contract, signed with the NEAR wallet.
//
// UTXO selection note: `buildWithdrawalPlan` is largest-first greedy (not
// bridge-sdk-rs's randomized `choose_utxos_random_no_payment`). The connector
// re-validates the inputs/outputs, so this is safe — selection is just
// deterministic rather than randomized.

import { ChainKind } from "@omni-bridge/core";
import { createBtcBuilder } from "@omni-bridge/btc";
import { createNearBuilder } from "@omni-bridge/near";

import { utxoWithdrawalInfo } from "@/api/client";
import { chains } from "@/config/chains";
import type { Network } from "@/config/network";
import type { UnsignedNearTx } from "@/types/dto";
import { sdkTxToUnsignedNearTx } from "@/wallet/sdkTx";

/** Form's PascalCase ChainKind name → enum value. `buildUtxoWithdrawalSubmit`
 *  converts a numeric origin_chain back to its canonical serde name
 *  (`ChainKind[n]`), so passing the enum value fixes the Polygon→Pol rename. */
const NAME_TO_CHAIN_KIND: Record<string, ChainKind> = {
  Near: ChainKind.Near,
  Eth: ChainKind.Eth,
  Sol: ChainKind.Sol,
  Arb: ChainKind.Arb,
  Base: ChainKind.Base,
  Bnb: ChainKind.Bnb,
  Polygon: ChainKind.Pol,
  Abs: ChainKind.Abs,
};

export interface SubmitBtcArgs {
  network: Network;
  /** UTXO destination chain ("Btc" | "Zcash"). */
  chain: "Btc" | "Zcash";
  /** The init_transfer NEAR tx hash. */
  nearTxHash: string;
  /** Account that signed the init_transfer tx. */
  senderId: string;
  /** Optional fee-rate override (sat/vB). */
  feeRate: number | null;
  signerId: string;
}

export async function buildSubmitBtcTransfer(args: SubmitBtcArgs): Promise<{ tx: UnsignedNearTx }> {
  const { network, signerId } = args;
  const chainSlug = args.chain === "Zcash" ? "zcash" : "btc";

  // 1. Backend scrape of the origin init_transfer tx.
  const info = await utxoWithdrawalInfo(args.nearTxHash, args.senderId);
  const netAmount = BigInt(info.net_amount);
  if (netAmount <= 0n) {
    throw new Error(`Net amount after fee is ${info.net_amount}; nothing to submit.`);
  }

  // 2. Connector state (change address + input cap) and available UTXOs.
  const near = createNearBuilder({ network });
  const config = await near.getUtxoConnectorConfig(chainSlug);
  const utxos = await near.getUtxoAvailableOutputs(chainSlug);
  if (!utxos.length) {
    throw new Error(`Connector reports no available UTXOs for ${chainSlug} — can't fund the withdrawal.`);
  }

  // 3. Plan inputs/outputs via @omni-bridge/btc (largest-first greedy).
  const rpcUrl = args.chain === "Zcash" ? chains(network).zcash.rpcUrl : chains(network).btc.rpcUrl;
  const btc = createBtcBuilder({ network, chain: chainSlug, rpcUrl });
  const plan = btc.buildWithdrawalPlan(
    utxos,
    netAmount,
    info.target_address,
    config.change_address,
    args.feeRate ?? undefined,
    config.max_withdrawal_input_number !== undefined
      ? { maxInputs: config.max_withdrawal_input_number }
      : undefined,
  );

  // 4. Build the submit call on the omni-bridge contract.
  const originChain = NAME_TO_CHAIN_KIND[info.origin_chain];
  if (originChain === undefined) {
    throw new Error(
      `Unexpected origin chain "${info.origin_chain}" from the init-transfer scrape — refusing to guess the transfer_id.`,
    );
  }
  const unsigned = near.buildUtxoWithdrawalSubmit({
    chain: chainSlug,
    transferId: { origin_chain: originChain, origin_nonce: String(info.origin_nonce) },
    targetAddress: info.target_address,
    inputs: plan.inputs,
    outputs: plan.outputs,
    maxGasFee: info.max_gas_fee !== undefined ? BigInt(info.max_gas_fee) : undefined,
    signerId,
  });
  return { tx: sdkTxToUnsignedNearTx(unsigned) };
}
